import { captureException } from '@sentry/vue'

/**
 * API呼び出しエラーの詳細情報を保持するクラス
 */
export class ApiError extends Error {
  public readonly url: string
  public readonly method: string
  public readonly status: number
  public readonly responseBody?: any
  public readonly requestBody?: unknown

  constructor(config: {
    url: string
    method: string
    status: number
    message?: string
    responseBody?: unknown
    requestBody?: unknown
  }) {
    const message =
      config.message ||
      `API Error: ${config.method} ${config.url} returned ${config.status}`
    super(message)

    this.name = 'ApiError'
    this.url = config.url
    this.method = config.method
    this.status = config.status
    this.responseBody = config.responseBody
    this.requestBody = config.requestBody

    // スタックトレースを正しく設定
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError)
    }
  }

  /**
   * エラー情報をSentryに送信
   */
  reportToSentry(): void {
    captureException(this, {
      contexts: {
        api: {
          url: this.url,
          method: this.method,
          status: this.status,
          responseBody: this.responseBody,
          requestBody: this.requestBody
        }
      },
      tags: {
        errorType: 'api_error',
        httpStatus: this.status.toString(),
        apiEndpoint: this.url
      }
    })
  }

  /**
   * エラー情報をオブジェクトとして取得
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      url: this.url,
      method: this.method,
      status: this.status,
      responseBody: this.responseBody,
      requestBody: this.requestBody
    }
  }
}

/**
 * API呼び出しでエラーが発生した場合のエラーハンドリング
 * エラー情報をSentryに送信し、ApiErrorをthrowする
 *
 * options.skipSentryStatuses に指定したHTTPステータスは「想定内シグナル」として
 * Sentryへ送信せず、ApiErrorのthrowだけ行う（呼び出し側でリダイレクト等に変換する用途）。
 * 例: 認証必須APIの 401（認証切れ）をノイズとして報告しない。
 */
export async function handleApiError(
  response: Response,
  config: {
    url: string
    method: string
    requestBody?: unknown
  },
  options?: {
    skipSentryStatuses?: number[]
  }
): Promise<never> {
  let responseBody: unknown
  try {
    responseBody = await response.json()
  } catch {
    responseBody = null
  }

  const error = new ApiError({
    url: config.url,
    method: config.method,
    status: response.status,
    responseBody,
    requestBody: config.requestBody
  })

  // 想定内ステータス（例: 401 認証切れ）はSentryノイズになるので送信しない
  if (!options?.skipSentryStatuses?.includes(response.status)) {
    error.reportToSentry()
  }

  throw error
}
